import { useEffect, useState } from "react";
import { apiGet, prettifyApiError } from "@/shared/api";
import { useReturnDialog } from "@/features/resi/useReturnDialog";
import type { ReturnRecord } from "@/features/resi/api";
import type { Prenotazione } from "@/features/ritiri/api";
import { Chiamate, Cliente, Soldi } from "./contesto/Cliente";
import { Fornitore } from "./contesto/Fornitore";
import { Pratiche, SpedizioneOriginale } from "./contesto/Pacchi";
import { Prodotti } from "./contesto/Prodotti";
import { Testata } from "./contesto/Testata";
import { PRATICA_APERTA, type ThreadContextResponse } from "./contesto/tipi";

export type { ParcelInfo } from "./contesto/tipi";

interface ContextPanelProps {
  /** ID thread selezionato. Quando ``null`` (nessuna chat aperta) il
   *  pannello mostra placeholder vuoto invece di unmount. */
  threadId: number | null;
  /** Chiamato dopo la creazione di un reso/sostituzione dal contesto ordine,
   *  col record creato. ``MessaggiPage`` lo usa per, sul RESO, precompilare il
   *  composer col template + allegare l'etichetta (bozza, invio manuale). */
  onReturnCreated?: (record: ReturnRecord, ritiro?: Prenotazione | null) => void;
  /** Avviso tracking di una sostituzione inviato dal dialog: la chat va
   *  ricaricata, o il messaggio appena partito non si vede. */
  onMessaggioInviato?: () => void;
}

/**
 * Ispettore Messaggi — colonna contesto.
 *
 * Rifatto il 2026-10-14 (Davide: «mancano troppe info utili e vanno scritte
 * piu' chiaramente»). Testata fissa (canale, cliente, N° ordine, data ordine,
 * stato, scadenza), sotto le sezioni nell'ordine delle domande della chat, in
 * fondo i tasti. Testo nero ovunque; il colore e' solo stato.
 *
 * Quando l'ordine non e' ancora stato sincronizzato mostra solo i metadati
 * base + hint operativo.
 */
export function ContextPanel({ threadId, onReturnCreated, onMessaggioInviato }: ContextPanelProps) {
  const [data, setData] = useState<ThreadContextResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { openFor, dialog, busyOrder, openError } = useReturnDialog(
    onReturnCreated,
    onMessaggioInviato,
  );

  useEffect(() => {
    if (threadId == null) {
      setData(null);
      setError(null);
      return;
    }
    // Audit 2026-05-29: AbortController per cancellare la fetch del contesto
    // al cambio thread (prima c'era solo il flag ``cancelled``: la richiesta
    // restava in volo). ``cancelled`` resta come guardia del setState anche
    // se apiGet ignorasse il signal (es. mock nei test).
    const controller = new AbortController();
    let cancelled = false;
    setLoading(true);
    setError(null);
    void apiGet<ThreadContextResponse>(`/api/mail/threads/${threadId}/context`, {
      signal: controller.signal,
    })
      .then((res) => {
        if (!cancelled) {
          setData(res);
          setLoading(false);
        }
      })
      .catch((err: unknown) => {
        if (cancelled || controller.signal.aborted) return;
        setError(prettifyApiError(err));
        setLoading(false);
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [threadId]);

  if (threadId == null) {
    return (
      <aside className="flex min-h-0 flex-col border-l border-line bg-paper-2">
        <div className="border-b border-line px-4 py-3 text-[14px] font-bold text-ink">
          Contesto
        </div>
        <div className="flex flex-1 items-center justify-center px-4 text-center text-[12px] text-ink italic">
          Seleziona una conversazione per vedere il contesto
        </div>
      </aside>
    );
  }

  return (
    <aside className="flex min-h-0 flex-col overflow-hidden border-l border-line bg-paper-2">
      {data && !loading && !error ? (
        <Testata data={data} />
      ) : (
        <div className="border-b border-line px-4 py-3 text-[14px] font-bold text-ink">
          Contesto ordine
        </div>
      )}
      <div className="flex-1 space-y-3 overflow-y-auto p-3">
        {loading && <div className="text-[12px] italic text-ink">Caricamento…</div>}
        {error && <div className="text-[12px] text-red">{error}</div>}
        {/* Errore del prefill reso/sostituzione: senza, il pulsante resterebbe
            muto se il prefill fallisce. Regola: ogni pulsante dà feedback. */}
        {openError && <div className="text-[12px] text-red">{openError}</div>}
        {data && !loading && !error && <ContextBody data={data} />}
      </div>
      {data && !loading && !error && <Azioni data={data} openFor={openFor} busyOrder={busyOrder} />}
      {dialog}
    </aside>
  );
}

interface AzioniProps {
  data: ThreadContextResponse;
  openFor: (orderRef: string, kind: "reso" | "sostituzione") => Promise<void>;
  busyOrder: string | null;
}

const BOTTONE =
  "flex-1 rounded-sm border border-line-2 bg-card px-2 py-[7px] text-center text-[12px] font-semibold text-ink hover:border-accent hover:bg-paper disabled:opacity-50";

/** Ordine delle sezioni = ordine delle domande in chat (Davide 2026-10-14):
 *  dov'e' il pacco, cosa fa il fornitore, cosa ha comprato, chi e', i soldi.
 *  Una pratica (reso/sostituzione) ancora in viaggio sale in cima. */
function ContextBody({ data }: { data: ThreadContextResponse }) {
  const order = data.order;
  if (!order) {
    return data.external_order_id ? (
      <div className="rounded-md border border-amber/20 bg-amber-soft px-3 py-2 text-[12px] text-ink">
        Ordine <span className="font-bold tabular-nums">{data.external_order_id}</span> non ancora
        sincronizzato. Forza un sync ordini per popolare il contesto.
      </div>
    ) : (
      <div className="rounded-md border border-line bg-paper px-3 py-2 text-[12px] text-ink">
        Thread senza ordine collegato.
      </div>
    );
  }
  const returns = data.returns ?? [];
  const inViaggio = returns.some((r) => PRATICA_APERTA.has(r.status));
  const pratiche = <Pratiche returns={returns} evidenza={inViaggio} />;
  return (
    <>
      {inViaggio && pratiche}
      <SpedizioneOriginale
        order={order}
        parcels={data.parcels ?? []}
        trackingUrl={data.tracking_url ?? ""}
        titolo={returns.length > 0 ? "Spedizione originale" : "Spedizione"}
      />
      <Fornitore order={order} riordini={data.supplier_replacements ?? []} />
      {!inViaggio && pratiche}
      <Prodotti items={order.items} />
      <Cliente order={order} data={data} />
      <Soldi data={data} currency={order.currency} />
      <Chiamate calls={data.calls ?? []} />
    </>
  );
}

/** Tasti sempre a portata, fuori dallo scroll. Aprono il ReturnDialog con
 *  prefill dal backend; disabilitati mentre il prefill e' in volo. */
function Azioni({ data, openFor, busyOrder }: AzioniProps) {
  const ref = data.order?.reference;
  if (!ref) return null;
  return (
    <div className="flex gap-1.5 border-t border-line bg-card px-3 py-2">
      <button
        type="button"
        disabled={busyOrder === ref}
        onClick={() => void openFor(ref, "reso")}
        className={BOTTONE}
      >
        Crea reso
      </button>
      <button
        type="button"
        disabled={busyOrder === ref}
        onClick={() => void openFor(ref, "sostituzione")}
        className={BOTTONE}
      >
        Crea sostituzione
      </button>
    </div>
  );
}
